import React, { useState } from 'react';
import { manualTrailerApi } from '../api';

export default function ManageTrailers({ subAccountId, trackers, onTrailersChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [editingTrailer, setEditingTrailer] = useState(null);
  const [linkingTrailer, setLinkingTrailer] = useState(null);
  const [selectedRidedogId, setSelectedRidedogId] = useState('');
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });

  const manualTrailers = trackers.filter(t => t.manual);
  const ridedogTrailers = trackers.filter(t => !t.manual);

  const openNewForm = () => {
    setEditingTrailer(null);
    setForm({ name: '', description: '' });
    setShowForm(true);
  };

  const openEditForm = (trailer) => {
    setEditingTrailer(trailer);
    setForm({ name: trailer.name, description: trailer.description || '' });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTrailer) {
        await manualTrailerApi.update(subAccountId, editingTrailer.id, form.name, form.description);
      } else {
        await manualTrailerApi.add(subAccountId, form.name, form.description);
      }
      setShowForm(false);
      onTrailersChanged();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trailer) => {
    if (!window.confirm(`Remove "${trailer.name}"? This will also delete its service log entries.`)) return;
    await manualTrailerApi.remove(subAccountId, trailer.id).catch(() => {});
    onTrailersChanged();
  };

  const handleLink = async (trailer) => {
    if (!selectedRidedogId) { alert('Please select a RideDog tracker.'); return; }
    const ridedogTracker = ridedogTrailers.find(t => t.id === selectedRidedogId);
    if (!window.confirm(
      `Link "${trailer.name}" to RideDog tracker "${ridedogTracker?.name}"?\n\nGPS data from "${ridedogTracker?.name}" will be associated with "${trailer.name}". Your service log history stays with "${trailer.name}".`
    )) return;
    setLinking(true);
    try {
      await manualTrailerApi.link(subAccountId, trailer.id, selectedRidedogId, ridedogTracker?.name);
      setLinkingTrailer(null);
      setSelectedRidedogId('');
      onTrailersChanged();
    } catch (err) {
      alert('Failed to link: ' + err.message);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (trailer) => {
    if (!window.confirm(`Unlink "${trailer.name}" from its RideDog tracker? GPS data will no longer be associated.`)) return;
    try {
      await manualTrailerApi.unlink(subAccountId, trailer.id);
      onTrailersChanged();
    } catch (err) {
      alert('Failed to unlink: ' + err.message);
    }
  };

  return (
    <div className="alerts-panel">
      <div className="alerts-header">
        <div>
          <h2>Manage Trailers</h2>
          <p>Add trailers manually or link them to RideDog GPS trackers.</p>
        </div>
        <button className="btn-primary" onClick={openNewForm}>+ Add Trailer</button>
      </div>

      {showForm && (
        <div className="alert-form-card">
          <h3>{editingTrailer ? 'Edit Trailer' : 'Add Trailer'}</h3>
          <form onSubmit={handleSave}>
            <div className="form-row">
              <div className="form-group">
                <label>Trailer name</label>
                <input type="text" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. B6" required autoFocus />
              </div>
              <div className="form-group">
                <label>Description <span className="field-hint">(optional)</span></label>
                <input type="text" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. 2021 PJ Car Hauler" />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Trailer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* RideDog GPS tracked trailers */}
      {ridedogTrailers.length > 0 && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
            RideDog GPS Tracked
          </div>
          <div className="alerts-list" style={{ marginBottom: '20px' }}>
            {ridedogTrailers.map(t => (
              <div key={t.id} className="alert-row">
                <div className="alert-row-main">
                  <div className="alert-tracker-name">{t.name}</div>
                  <div className="alert-meta" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                    <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                      Live GPS
                    </span>
                    {t.total_miles > 0 && <span>{Math.round(t.total_miles).toLocaleString()} mi</span>}
                    {t.state_label && <span style={{ color: 'var(--slate-400)', fontSize: '12px' }}>{t.state_label}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual trailers */}
      <div>
        <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
          Manually Added
        </div>
        {manualTrailers.length === 0 && !showForm ? (
          <div className="empty-state">
            <span className="empty-icon">🚛</span>
            <h3>No trailers added yet</h3>
            <p>Add trailers to start logging their maintenance history.</p>
            <button className="btn-primary" onClick={openNewForm}>Add First Trailer</button>
          </div>
        ) : (
          <div className="alerts-list">
            {manualTrailers.map(t => {
              const linkedTracker = ridedogTrailers.find(r => r.id === t.linked_ridedog_id);
              return (
                <div key={t.id} className="alert-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div className="alert-row-main">
                      <div className="alert-tracker-name">{t.name}</div>
                      {t.description && <div className="alert-meta">{t.description}</div>}
                      <div style={{ marginTop: '4px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {linkedTracker ? (
                          <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                            Linked to {linkedTracker.name}
                          </span>
                        ) : (
                          <span style={{ background: 'var(--slate-100)', color: 'var(--slate-500)', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                            No GPS tracker
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="alert-row-actions" style={{ flexShrink: 0 }}>
                      {linkedTracker ? (
                        <button className="btn-ghost" style={{ fontSize: '12px', padding: '5px 10px' }}
                          onClick={() => handleUnlink(t)}>
                          Unlink
                        </button>
                      ) : ridedogTrailers.length > 0 && (
                        <button className="btn-ghost" style={{ fontSize: '12px', padding: '5px 10px', whiteSpace: 'nowrap' }}
                          onClick={() => { setLinkingTrailer(t.id === linkingTrailer ? null : t.id); setSelectedRidedogId(''); }}>
                          Link to RideDog
                        </button>
                      )}
                      <button className="btn-icon" onClick={() => openEditForm(t)} title="Edit">✏️</button>
                      <button className="btn-icon danger" onClick={() => handleDelete(t)} title="Delete">🗑</button>
                    </div>
                  </div>

                  {/* Link panel */}
                  {linkingTrailer === t.id && (
                    <div style={{
                      background: 'var(--slate-50)', border: '1px solid var(--slate-200)',
                      borderRadius: '8px', padding: '14px', display: 'flex', gap: '10px', alignItems: 'flex-end',
                    }}>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--slate-600)', marginBottom: '5px', display: 'block' }}>
                          Select RideDog tracker to link
                        </label>
                        <select value={selectedRidedogId} onChange={e => setSelectedRidedogId(e.target.value)}
                          style={{ padding: '8px 10px', border: '1px solid var(--slate-200)', borderRadius: '6px', fontSize: '13px', width: '100%' }}>
                          <option value="">Select a tracker...</option>
                          {ridedogTrailers.map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                        <p style={{ fontSize: '11px', color: 'var(--slate-400)', marginTop: '5px' }}>
                          GPS data from the selected tracker will be associated with this trailer. Your service log history stays here.
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <button className="btn-ghost" onClick={() => setLinkingTrailer(null)}>Cancel</button>
                        <button className="btn-primary" onClick={() => handleLink(t)} disabled={!selectedRidedogId || linking}>
                          {linking ? 'Linking...' : 'Link'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
